// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the peek
//  ---------------------------------------------------------------------------
//  On a desktop the day is a 420pt panel at the leading edge and the board has
//  the rest of the window. The panel folds away so the board can have all of
//  it — and folding used to be a COMMITMENT: the week strip, the countdown,
//  every record and the place you write from all went behind one control you
//  had to find, press, and press again, with a drawing surface re-laid-out
//  both times.
//
//  This module is the other way back. Lean the pointer into the edge the panel
//  went behind and the day comes out over the board for as long as you are
//  looking at it. Move away and it is gone. Nothing is written: the fold is a
//  setting, a glance is not, and the only path in this app that changes the
//  fold is still the one where you press the thing.
//
//  ── WHY THE RULES LIVE HERE AND NOT IN THE SCREEN ──────────────────────────
//  md-app.jsx is the app: it is never mounted by the suite, only read as
//  source, so a rule written there can only be asserted as a shape. The rules
//  with edges worth arguing about — fourteen pixels and not fifteen, the
//  LEADING edge and not the left one, a held button that is a stroke and not a
//  request — are decisions, and decisions belong where a test can call them.
//
//  ── WHY NOTHING IS PLACED AT THE EDGE ──────────────────────────────────────
//  The obvious build is an invisible strip with onMouseEnter. It cannot be
//  used: the board's own Style handle is pinned at max(8px, …) from that same
//  edge and its style panel hangs below it, so a strip would sit on top of
//  controls the drawing surface owns and eat their clicks. Watching where the
//  pointer IS costs one passive listener and stands in nothing's way.
//
//  ── WHY `contains` AND NOT A RECTANGLE ─────────────────────────────────────
//  A peeked panel is a real element the pointer is really over, so the DOM can
//  simply be asked whether the event landed inside it. That is cheaper than
//  measuring, it cannot go stale between a resize and the next frame, and it
//  keeps working when the panel is mid-animation — a rectangle read at 40% of
//  the reveal would say the pointer is outside a panel it is plainly on.
//
//  Exposes MD_PEEK, mdPeekArmed, mdPeekHolds and useMdPeek on window. Loaded
//  after md-layout.jsx, whose `fine` answer decides whether the peek exists at
//  all, and before md-app.jsx, which reads all four at module scope.
// ════════════════════════════════════════════════════════════════════════════

/** Where the pointer is, and what it is doing while it is there. */
interface MdPeekProbe {
  /** clientX, in the layout viewport the band is measured against. */
  x: number;
  /** The window's own width — `w - x` is the distance to the trailing edge. */
  w: number;
  /** Right-to-left, so the leading edge is the right-hand one. */
  rtl: boolean;
  /** The `buttons` bitmask of the move. Anything set means a drag is in flight. */
  buttons: number;
  /** The pointer is on the folded panel's surviving pill. */
  overPill: boolean;
}

/** The four reasons an open peek must not retract, and the one that cancels
    the weakest of them. */
interface MdPeekHold {
  overPanel: boolean;
  /** Still leaning on the edge that summoned it — see mdPeekHolds. */
  overBand: boolean;
  focusInside: boolean;
  /** The day has opened something full-screen inside itself. */
  sheetOpen: boolean;
  /** A window stands in the workspace, over the day rather than inside it. */
  covered?: boolean;
}

interface MdPeekOpts {
  /** A pointer that hovers, a canvas beside the day, and the day folded away. */
  enabled: boolean;
  panelRef: { current: any };
  pillRef: { current: any };
  sheetOpen: boolean;
  /** Writing on the board outranks a glance at the day. */
  suppressed: boolean;
  /** A window stands over the day — see the focus clause in mdPeekHolds. */
  covered?: boolean;
}

(function (g: any) {
  const React = g.React;

  /* Fourteen: wide enough to be reachable by throwing the pointer at the edge,
     narrow enough that it is a place you go rather than a place you pass
     through. Ninety and two-sixty are not symmetric on purpose — arriving
     should feel like an answer, leaving should forgive a crossed corner. */
  const MD_PEEK = { edge: 14, openMs: 90, closeMs: 260 };

  /** Is the pointer, where it is now, asking for the day? */
  function mdPeekArmed(probe: MdPeekProbe): boolean {
    const { x, w, rtl, buttons, overPill } = probe;
    /* First, and above everything else. A stroke dragged off the side of the
       board is a stroke; the day sliding in under a drawing being made is the
       single worst thing this feature could do. */
    if (buttons) return false;
    /* The pill is the panel's own remnant, so reaching for it IS the gesture.
       It arms wherever it stands rather than getting a second band. */
    if (overPill) return true;
    // A pointer past either edge has left the window — during a capture, and
    // on the way out — and is not leaning into anything.
    if (!(x >= 0 && x <= w)) return false;
    return rtl ? w - x <= MD_PEEK.edge : x <= MD_PEEK.edge;
  }

  /** Must an open peek stay open?

      `overBand` is not a nicety. A peeked panel floats 14px in from the edge —
      the same 14px the band occupies — so a pointer resting in the band is
      never over the panel. Without this clause the very next move a hand makes
      while holding steady at the edge reports the panel left, schedules the
      close, and the retracting panel re-arms the band underneath it: the day
      flickers in and out of a corner nobody moved away from. Leaning on the
      edge that summoned the panel is still asking for it.

      `covered` cancels exactly one clause, and only the weakest. FOCUS INSIDE
      A COVERED DAY IS RESIDUE, NOT INTENT: the caret holds the peek so that
      reaching in and typing survives the pointer drifting off the panel, and
      that reasoning ends when a window opens over the workspace. The caret is
      then where you WERE when you opened the window — nothing in the app moves
      it afterwards — so the day stood out over the whole screen until the
      window was closed. Reported on a desktop, 2026-08-28: peek, open Projects
      from Tabs inside the peeked day, and no movement of the pointer ever
      brought the day back.

      The pointer's two clauses are untouched: pointing at the day is a live
      intent whether or not something stands over it. So is `sheetOpen` — a
      confirm or a reschedule stands ABOVE the workspace and belongs to the
      day, and taking the day away from under it is the older bug this file
      already carries a paragraph about. */
  function mdPeekHolds(hold: MdPeekHold): boolean {
    return !!(hold.overPanel || hold.overBand
      || (hold.focusInside && !hold.covered) || hold.sheetOpen);
  }

  /* ── the watcher ─────────────────────────────────────────────────────────
     One `pointermove` listener, passive and in the capture phase so nothing
     downstream can stop it from reaching us. It is re-subscribed only when
     something about the ANSWER changes, never per move. Escape rides the
     dismiss arbiter's stack, registered above.

     The returned `peeking` is DERIVED, not just stored: `enabled` going false
     or a record opening on the board must take the panel away in the same
     render that changed them, not one effect later. A state round trip would
     paint one frame of a peek that has already been overruled. */
  function useMdPeek(opts: MdPeekOpts) {
    const { enabled, panelRef, pillRef, sheetOpen, suppressed, covered } = opts;
    const [peeking, setPeeking] = React.useState(false);
    const timer = React.useRef(null);

    const clear = React.useCallback(() => {
      if (timer.current != null) { clearTimeout(timer.current); timer.current = null; }
    }, []);

    const close = React.useCallback(() => { clear(); setPeeking(false); }, [clear]);

    /* An open peek is a layer on the dismiss arbiter (md-dismiss.jsx), standing
       only while nothing the day opened inside itself stands above it — a peek
       that held the key under an open Settings sheet would close the panel out
       from under the sheet instead of closing the sheet. */
    g.useMdDismissLayer(close, !!(peeking && enabled && !suppressed && !sheetOpen));

    React.useEffect(() => {
      if (!enabled || suppressed) { clear(); setPeeking(false); return; }

      const inside = (ref: { current: any }, target: any) =>
        !!(ref.current && target && ref.current.contains(target));

      const onMove = (e: any) => {
        const overPanel = inside(panelRef, e.target);
        const overPill = inside(pillRef, e.target);
        const armed = mdPeekArmed({
          x: e.clientX,
          w: g.innerWidth,
          rtl: g.document.documentElement.dir === 'rtl',
          buttons: e.buttons,
          overPill,
        });
        if (peeking) {
          const focusInside = inside(panelRef, g.document.activeElement);
          if (mdPeekHolds({ overPanel, overBand: armed, focusInside, sheetOpen, covered })) { clear(); return; }
          // Already leaving; the countdown that is running is the honest one.
          if (timer.current != null) return;
          timer.current = setTimeout(() => {
            timer.current = null;
            setPeeking(false);
          }, MD_PEEK.closeMs);
          return;
        }
        // Leaving the band cancels the intent: crossing a corner on the way
        // somewhere else is not a request for the day.
        if (!armed) { clear(); return; }
        if (timer.current != null) return;
        timer.current = setTimeout(() => {
          timer.current = null;
          setPeeking(true);
        }, MD_PEEK.openMs);
      };

      g.addEventListener('pointermove', onMove, { capture: true, passive: true });
      return () => {
        g.removeEventListener('pointermove', onMove, { capture: true } as any);
        clear();
      };
    }, [enabled, suppressed, sheetOpen, covered, peeking, panelRef, pillRef, clear, close]);

    return { peeking: peeking && enabled && !suppressed, close };
  }

  g.MD_PEEK = MD_PEEK;
  Object.assign(g, { mdPeekArmed, mdPeekHolds, useMdPeek });
})(typeof window !== 'undefined' ? window : globalThis);
